import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api-client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // List data in a CRM goes stale quickly, but not within a few seconds of
      // a user tabbing between views.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Retrying an authorization or validation failure just repeats it.
        if (error instanceof ApiError && error.status < 500 && error.status !== 429) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // A failed write must surface to the user, not be silently repeated.
      retry: false,
    },
  },
});

/**
 * Query keys in one place, so invalidation never guesses at a string.
 *
 * Each module exposes `all` as the prefix every one of its keys starts with,
 * which makes "invalidate everything about deals" a single call that also
 * catches the board and the forecast.
 */
export const queryKeys = {
  session: ['session'] as const,
  users: {
    all: ['users'] as const,
    list: (params: Record<string, unknown>) => ['users', 'list', params] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },
  organization: ['organization'] as const,
  auditLogs: (params: Record<string, unknown>) => ['audit-logs', params] as const,

  leads: {
    all: ['leads'] as const,
    list: (params: Record<string, unknown>) => ['leads', 'list', params] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
  },
  accounts: {
    all: ['accounts'] as const,
    list: (params: Record<string, unknown>) => ['accounts', 'list', params] as const,
    detail: (id: string) => ['accounts', 'detail', id] as const,
    options: (search: string) => ['accounts', 'options', search] as const,
    financialSummary: (id: string) => ['accounts', 'financial-summary', id] as const,
  },
  contacts: {
    all: ['contacts'] as const,
    list: (params: Record<string, unknown>) => ['contacts', 'list', params] as const,
    detail: (id: string) => ['contacts', 'detail', id] as const,
    options: (search: string, accountId?: string) =>
      ['contacts', 'options', search, accountId] as const,
  },
  deals: {
    all: ['deals'] as const,
    list: (params: Record<string, unknown>) => ['deals', 'list', params] as const,
    detail: (id: string) => ['deals', 'detail', id] as const,
    board: (params: Record<string, unknown>) => ['deals', 'board', params] as const,
    forecast: (params: Record<string, unknown>) => ['deals', 'forecast', params] as const,
    options: (search: string) => ['deals', 'options', search] as const,
  },
  activities: {
    all: ['activities'] as const,
    list: (params: Record<string, unknown>) => ['activities', 'list', params] as const,
    detail: (id: string) => ['activities', 'detail', id] as const,
    myDay: ['activities', 'my-day'] as const,
    calendar: (params: Record<string, unknown>) => ['activities', 'calendar', params] as const,
  },
  pipelines: ['pipelines'] as const,

  // Cross-cutting
  search: (params: Record<string, unknown>) => ['search', params] as const,
  notifications: {
    all: ['notifications'] as const,
    list: (params: Record<string, unknown>) => ['notifications', 'list', params] as const,
    counts: ['notifications', 'counts'] as const,
  },
  importDatasets: ['import', 'datasets'] as const,

  // Automation & insight
  workflows: {
    all: ['workflows'] as const,
    list: (params: Record<string, unknown>) => ['workflows', 'list', params] as const,
    detail: (id: string) => ['workflows', 'detail', id] as const,
    executions: (id: string) => ['workflows', 'executions', id] as const,
    metadata: ['workflows', 'metadata'] as const,
  },
  assignmentRules: {
    all: ['assignment-rules'] as const,
    list: ['assignment-rules', 'list'] as const,
  },
  approvals: {
    all: ['approvals'] as const,
    list: (params: Record<string, unknown>) => ['approvals', 'list', params] as const,
  },
  reports: {
    all: ['reports'] as const,
    dashboard: (params: Record<string, unknown>) => ['reports', 'dashboard', params] as const,
  },
  customFields: {
    all: ['custom-fields'] as const,
    list: (module: string | undefined) => ['custom-fields', 'list', module ?? 'all'] as const,
  },
  emailTemplates: {
    all: ['email-templates'] as const,
    list: (params: Record<string, unknown>) => ['email-templates', 'list', params] as const,
  },
  emails: {
    all: ['emails'] as const,
    list: (params: Record<string, unknown>) => ['emails', 'list', params] as const,
  },
  notes: (params: Record<string, unknown>) => ['notes', params] as const,
  timeline: (type: string, id: string) => ['timeline', type, id] as const,
  savedViews: {
    all: ['saved-views'] as const,
    list: (params: Record<string, unknown>) => ['saved-views', 'list', params] as const,
    fields: (module: string) => ['saved-views', 'fields', module] as const,
  },
} as const;

/**
 * The Zoho parity modules. Identical key shapes to the core records, built
 * from one factory so a new module cannot arrive with a subtly different one.
 */
function moduleKeys<TName extends string>(name: TName) {
  return {
    all: [name] as const,
    list: (params: Record<string, unknown>) => [name, 'list', params] as const,
    detail: (id: string) => [name, 'detail', id] as const,
    options: (search: string) => [name, 'options', search] as const,
  };
}

export const moduleQueryKeys = {
  vendors: moduleKeys('vendors'),
  products: moduleKeys('products'),
  campaigns: moduleKeys('campaigns'),
  cases: moduleKeys('cases'),
  solutions: moduleKeys('solutions'),
  quotes: moduleKeys('quotes'),
  salesOrders: moduleKeys('salesOrders'),
  invoices: moduleKeys('invoices'),
  payments: moduleKeys('payments'),
  expenses: moduleKeys('expenses'),
  recurringExpenses: moduleKeys('recurringExpenses'),
  creditNotes: moduleKeys('creditNotes'),
  bankAccounts: moduleKeys('bankAccounts'),
} as const;

export const bankingQueryKeys = {
  picker: ['bankAccounts', 'picker'] as const,
  transactions: (params: Record<string, unknown>) => ['bankTransactions', 'list', params] as const,
  overallReport: (params: Record<string, unknown>) =>
    ['bankingReports', 'overall', params] as const,
  accountReport: (params: Record<string, unknown>) =>
    ['bankingReports', 'account', params] as const,
};

/**
 * Record modules reference each other — a deal shows its account, an account
 * rolls up its deals — so a write in one invalidates the others' caches too.
 */
export const CRM_QUERY_PREFIXES = [
  queryKeys.leads.all,
  queryKeys.accounts.all,
  queryKeys.contacts.all,
  queryKeys.deals.all,
  queryKeys.activities.all,
  // A quote shows its account, an invoice its order: the same argument that
  // makes the core modules invalidate together applies to these.
  ...Object.values(moduleQueryKeys).map((keys) => keys.all),
] as const;
